import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { camaraAPI } from '../services/api';
import { LegislativePeriod } from '../types';

const DEFAULT_PERIOD: LegislativePeriod = {
  id: '2026-2030',
  name: 'Periodo Legislativo 2026-2030',
  start_date: '2026-03-11',
  end_date: '2030-03-10',
  is_current: true,
};

interface PeriodContextType {
  periods: LegislativePeriod[];
  currentPeriod: LegislativePeriod | null;
  selectedPeriod: string;
  setSelectedPeriod: (periodId: string) => void;
  loading: boolean;
  error: boolean;
}

const PeriodContext = createContext<PeriodContextType>({
  periods: [],
  currentPeriod: null,
  selectedPeriod: DEFAULT_PERIOD.id,
  setSelectedPeriod: () => {},
  loading: true,
  error: false,
});

export const usePeriod = () => useContext(PeriodContext);

export const PeriodProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [periods, setPeriods] = useState<LegislativePeriod[]>([]);
  const [currentPeriod, setCurrentPeriod] = useState<LegislativePeriod | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // URL param takes priority, then resolved current period, then default.
  // Supports 'all' as a special value meaning no period filter.
  const urlPeriodo = searchParams.get('periodo');
  const selectedPeriod = urlPeriodo || currentPeriod?.id || DEFAULT_PERIOD.id;

  useEffect(() => {
    const fetchPeriods = async () => {
      try {
        const data = await camaraAPI.getPeriods();
        setPeriods(data);
        const current = data.find((p: LegislativePeriod) => p.is_current) || null;
        setCurrentPeriod(current);
      } catch (err) {
        console.error('Failed to fetch periods:', err);
        setError(true);
        setPeriods([DEFAULT_PERIOD]);
        setCurrentPeriod(DEFAULT_PERIOD);
      } finally {
        setLoading(false);
      }
    };
    fetchPeriods();
  }, []);

  const setSelectedPeriod = useCallback((periodId: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (periodId === currentPeriod?.id) {
      newParams.delete('periodo');
    } else if (periodId === 'all') {
      newParams.set('periodo', 'all');
    } else {
      newParams.set('periodo', periodId);
    }
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams, currentPeriod]);

  return (
    <PeriodContext.Provider value={{ periods, currentPeriod, selectedPeriod, setSelectedPeriod, loading, error }}>
      {children}
    </PeriodContext.Provider>
  );
};
